import React, { useLayoutEffect, useRef, useState } from "react";
import { useDndContext } from "@dnd-kit/core";
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { ISorting } from "../../../interfaces/Components/ITable";
import SortAsending from "../../Atomos/Icons/SortAsending";
import SortDecending from "../../Atomos/Icons/SortDecending";
import SortDefault from "../../Atomos/Icons/SortDefault";
import { IHeader } from "./FilteredTable.types";
import HeaderLabelWithTooltip, {
  getHeaderAriaLabel,
} from "./HeaderLabelWithTooltip";
import { useStaffMode } from "../../../hooks/StaffModeProvider";

function ColumnResizeHandle({
  onResize,
  columnId,
  minWidth,
}: {
  onResize: (columnId: string, newWidth: number) => void;
  columnId: string;
  minWidth?: number;
}) {
  const handleRef = useRef<HTMLDivElement>(null);
  const [isResizing, setIsResizing] = useState(false);

  React.useEffect(() => {
    if (!isResizing) {
      return;
    }

    const handleMouseMove = (e: MouseEvent) => {
      if (!handleRef.current) {
        return;
      }
      const th = handleRef.current.closest("th");
      if (!th) {
        return;
      }

      const rect = th.getBoundingClientRect();
      const minAllowedWidth = minWidth || 50;
      const newWidth = Math.max(minAllowedWidth, e.clientX - rect.left);
      onResize(columnId, newWidth);
    };

    const handleMouseUp = () => {
      setIsResizing(false);
    };

    document.addEventListener("mousemove", handleMouseMove);
    document.addEventListener("mouseup", handleMouseUp);
    document.body.style.cursor = "col-resize";
    document.body.style.userSelect = "none";

    return () => {
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
      document.body.style.cursor = "";
      document.body.style.userSelect = "";
    };
  }, [isResizing, columnId, onResize, minWidth]);

  return (
    <div
      ref={handleRef}
      onMouseDown={(e) => {
        e.preventDefault();
        e.stopPropagation();
        setIsResizing(true);
      }}
      className="absolute top-0 right-0 w-2 h-full cursor-col-resize hover:bg-blue-400 opacity-0 hover:opacity-100 transition-opacity z-20"
      style={{ touchAction: "none", marginRight: "-4px" }}
      aria-label="Resize column"
      role="separator"
      aria-orientation="vertical"
    />
  );
}

interface DraggableHeaderCellProps {
  header: IHeader;
  sorting: ISorting;
  onSortingClick: (headerId: string) => void;
  colWidthPx?: number;
  onResize?: (columnId: string, newWidth: number) => void;
  useFixedLayout?: boolean;
  isFirstColumn?: boolean;
  isLastColumn?: boolean;
  rowSpan?: number;
  isSubHeader?: boolean;
  groupDragIds?: string[];
  isLastInGroup?: boolean;
  /** Split header/body scroll (e.g. firm logs modal): th sticky is redundant and clips with overflow-x-hidden. */
  disableSticky?: boolean;
}

export default function DraggableHeaderCell({
  header,
  sorting,
  onSortingClick,
  colWidthPx,
  onResize,
  useFixedLayout,
  isFirstColumn = false,
  isLastColumn = false,
  rowSpan,
  isSubHeader = false,
  groupDragIds,
  isLastInGroup = false,
  disableSticky = false,
}: DraggableHeaderCellProps) {
  const columnOrderFixed = !!header.columnOrderFixed;
  const { active } = useDndContext();
  const activeDragId = active?.id != null ? String(active.id) : null;
  const isGroupDragActive =
    groupDragIds != null &&
    activeDragId != null &&
    groupDragIds.includes(activeDragId);
  const { listeners, setNodeRef, transform, transition, isDragging } =
    useSortable({ id: header.id, disabled: columnOrderFixed });
  const contentRef = useRef<HTMLDivElement>(null);
  const [minContentWidth, setMinContentWidth] = useState<number | undefined>(
    undefined,
  );

  const horizontalPaddingPx =
    isFirstColumn && isLastColumn
      ? 32
      : isFirstColumn
        ? 20
        : isLastColumn
          ? 20
          : 8;

  const sortingEnabled = header.enableSorting !== false;

  useLayoutEffect(() => {
    if (!contentRef.current || !onResize) {
      return;
    }

    const measure = () => {
      const contentRect = contentRef.current?.getBoundingClientRect();
      if (contentRect && contentRect.width > 0) {
        setMinContentWidth(Math.ceil(contentRect.width) + horizontalPaddingPx);
      }
    };

    requestAnimationFrame(() => {
      requestAnimationFrame(measure);
    });
  }, [
    onResize,
    horizontalPaddingPx,
    header.id,
    header.header,
    header.enableSorting,
  ]);

  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition: isDragging || isGroupDragActive ? "none" : transition,
    opacity: isDragging || isGroupDragActive ? 0.7 : 1,
    position: "relative",
    ...(useFixedLayout
      ? {}
      : {
          width: colWidthPx
            ? `${colWidthPx}px`
            : header.width && header.width > 0
              ? `${header.width}px`
              : undefined,
          minWidth: colWidthPx
            ? `${colWidthPx}px`
            : header.width && header.width > 0
              ? `${header.width}px`
              : undefined,
        }),
  };

  const isSorted = sorting.field === header.id;
  const hasCustomHeader = Boolean(header.headerContent);
  const canDragReorder =
    !columnOrderFixed && typeof header.header === "string" && !hasCustomHeader;

  const horizontalPaddingClass =
    isFirstColumn && isLastColumn
      ? "px-4"
      : isFirstColumn
        ? "pl-4 pr-1"
        : isLastColumn
          ? "pl-1 pr-4"
          : "px-1";

  // const stickyClass = rowSpan != null && rowSpan > 1 ? "" : "sticky top-0 z-10";
  const stickyClass =
    disableSticky || (rowSpan != null && rowSpan > 1)
      ? ""
      : "sticky top-0 z-10 hover:border-x border-x-white";

  const verticalPaddingClass = isSubHeader ? "py-2" : "py-3";
  const alignmentClass = isSubHeader ? "text-center" : "text-left";
  const contentJustifyClass = isSubHeader ? "justify-center" : "";
  const { isStaffMode } = useStaffMode();

  return (
    <th
      ref={setNodeRef}
      style={style}
      rowSpan={rowSpan}
      data-col-id={header.id}
      className={`${stickyClass} ${isStaffMode ? "bg-accent-Default-dark" : "bg-primary-dark"} ${verticalPaddingClass} ${alignmentClass} text-sm font-medium text-white ${isSubHeader ? "table-grouped-header-sub" : ""} ${isSubHeader && isLastInGroup ? "table-grouped-header-sub-end" : ""} ${horizontalPaddingClass}`}
      scope="col"
    >
      <div
        className={`flex items-center ${contentJustifyClass} ${useFixedLayout ? "overflow-hidden" : ""}`}
      >
        <div
          ref={contentRef}
          className={`flex items-center flex-shrink-0 gap-1 min-w-0 ${canDragReorder ? "cursor-move" : ""}`}
          aria-label={canDragReorder ? "Reorder column" : undefined}
          {...(canDragReorder ? listeners : {})}
        >
          <div className="flex items-center flex-shrink-0 whitespace-nowrap overflow-hidden text-ellipsis">
            {hasCustomHeader ? (
              header.headerContent
            ) : typeof header.header === "string" ? (
              <HeaderLabelWithTooltip
                label={header.header}
                tooltip={header.headerTooltip}
              />
            ) : (
              header.header
            )}
          </div>

          {sortingEnabled && !hasCustomHeader && (
            <button
              type="button"
              className="cursor-pointer flex items-center justify-center flex-shrink-0"
              aria-label={`Sort ${getHeaderAriaLabel(
                header.header,
                header.id,
                header.headerTooltip,
              )}`}
              onClick={() => onSortingClick(header.id)}
            >
              {isSorted ? (
                sorting.order === "asc" ? (
                  <SortAsending className="table-sort-icon" />
                ) : (
                  <SortDecending className="table-sort-icon" />
                )
              ) : (
                <span className="table-sort-icon">
                  <SortDefault />
                </span>
              )}
            </button>
          )}
        </div>

        {!isSubHeader && <div className="flex-1" />}
      </div>
      {onResize && (
        <ColumnResizeHandle
          onResize={onResize}
          columnId={header.id}
          minWidth={minContentWidth ?? 50}
        />
      )}
    </th>
  );
}
